<template>
  <v-form ref="agendamentoForm">
    <v-row>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="funcionario.cpf"
          label="CPF"
          class="search-text-field"
          placeholder="Busque seu CPF"
          outlined
          :loading="loadingCpf"
          v-mask="'###.###.###-##'"
          :rules="[
            () => $v.funcionario.cpf.required || 'Campo obrigatório',
            () => $v.funcionario.cpf.minLength || 'CPF inválido',
            v => validaCpf(v) || 'CPF inválido'
          ]"
        >
          <template v-slot:append>
            <div class="d-flex">
              <v-btn color="primary" :loading="loadingCpf">
                <v-icon @click="verificaCpf">mdi-magnify</v-icon>
              </v-btn>
            </div>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" md="8">
        <v-text-field
          :disabled="disableFields"
          v-model="funcionario.nome"
          label="Nome"
          outlined
          :rules="[
            () => $v.funcionario.nome.required || 'Campo obrigatório',
            () => $v.funcionario.nome.minLength || 'Nome inválido'
          ]"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          :disabled="disableFields"
          v-model="funcionario.sexo"
          label="Sexo"
          outlined
          append-icon="mdi-chevron-down-circle"
          :items="sexos"
          :rules="[() => $v.funcionario.sexo.required || 'Campo obrigatório']"
        ></v-select>
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          :disabled="disableFields"
          v-model="funcionario.dtNascimento"
          :rules="[
            () => $v.funcionario.dtNascimento.required || 'Campo obrigatório',
            () => $v.funcionario.dtNascimento.validDate || 'Data inválida',
            () => $v.funcionario.dtNascimento.minimal || 'O funcionário tem que ter pelo menos 14 anos'
          ]"
          outlined
          label="Data de Nascimento"
          v-mask="'##/##/####'"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          :disabled="disableFields"
          v-model="funcionario.dtAdmissao"
          :rules="[
            () => $v.funcionario.dtAdmissao.required || 'Campo obrigatório',
            () => $v.funcionario.dtAdmissao.validDate || 'Data inválida'
          ]"
          outlined
          label="Data de admissão"
          v-mask="'##/##/####'"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <CustomAutoComplete
          v-model="funcionario.empresa"
          :items="empresas"
          @on-search="loadEmpresas"
          @on-change="onChangeEmpresa"
          @loading="loadingEmpresas = $event"
          :loading="loadingEmpresas"
          itemValue="idEmpresa"
          itemText="razaoSocial"
          :returnObject="true"
          label="Digite o nome da sua empresa"
          noDataText="Ops! Não encontramos nenhuma empresa"
          placeholder="Busque por uma empresa"
          :disabled="disableFields"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-select
          v-if="unidades.length > 0"
          :disabled="disableFields"
          v-model="funcionario.unidade"
          label="Unidade"
          outlined
          @change="onChangeUnidade"
          item-value="idEmpresa"
          item-text="razaoSocial"
          :items="unidades"
          append-icon="mdi-chevron-down-circle"
          :rules="[v => validaUnidade(v) || 'Unidade obrigatória']"
        ></v-select>
      </v-col>
      <v-col cols="12" md="4" v-if="funcionario.empresa">
        <v-select
          :disabled="disableFields || funcoes.length === 0"
          v-model="funcionario.idFuncao"
          label="Função"
          outlined
          :items="funcoes"
          item-text="nome"
          item-value="idFuncao"
          :loading="loadingFuncoes"
          append-icon="mdi-chevron-down-circle"
          :rules="[() => $v.funcionario.idFuncao.required || 'Campo obrigatório']"
        ></v-select>
      </v-col>
      <v-col cols="12" md="4" v-if="funcionario.empresa">
        <v-select
          :disabled="disableFields || setores.length === 0"
          v-model="funcionario.idSetor"
          label="Setor"
          outlined
          :items="setores"
          :loading="loadingSetores"
          item-text="nome"
          item-value="idSetor"
          append-icon="mdi-chevron-down-circle"
          :rules="[() => $v.funcionario.idSetor.required || 'Campo obrigatório']"
        ></v-select>
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-model="funcionario.exame"
          label="Tipo de exame"
          outlined
          append-icon="mdi-chevron-down-circle"
          :items="exames"
          item-value="slug"
          :rules="[() => $v.funcionario.exame.required || 'Campo obrigatório']"
        ></v-select>
      </v-col>
      <v-col cols="12" class="justify-end d-flex">
        <v-btn color="primary" large rounded @click="nextStep()">
          Próximo
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <RedirectToLoginDialog v-if="openRedirectDialog" v-model="openRedirectDialog" />
  </v-form>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { required, minLength } from 'vuelidate/lib/validators'
import { dateRegex, SEXOS, TIPOS_EXAME } from '@/utils/constants'
import validaCpf from '@/utils/validaCpf'
import CustomAutoComplete from '@/core/components/CustomAutoComplete'
import acessoRapidoService from '@/services/acessoRapido'
import RedirectToLoginDialog from './dialogs/RedirectToLoginDialog'
import minimalDate from '../../utils/minimalDate'
import { parse } from 'date-fns'

export default {
  name: 'AgendamentoForm',
  props: {
    value: {
      type: Object,
      required: true
    }
  },
  computed: {
    funcionario: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    }
  },
  mixins: [validationMixin],
  validations: {
    funcionario: {
      nome: {
        required,
        minLength: minLength(3)
      },
      cpf: {
        required,
        minLength: minLength(14)
      },
      dtNascimento: {
        required,
        validDate(dtNascimento) {
          return dateRegex.test(dtNascimento)
        },
        minimal(dt_nascimento) {
          const dataNascimento = parse(dt_nascimento, 'dd/MM/yyyy', new Date())
          return minimalDate(dataNascimento, 14)
        }
      },
      dtAdmissao: {
        required,
        validDate(dtAdmissao) {
          return dateRegex.test(dtAdmissao)
        }
      },
      sexo: {
        required
      },
      exame: {
        required
      },
      idFuncao: {
        required
      },
      idSetor: {
        required
      }
    }
  },
  data() {
    return {
      funcoes: [],
      setores: [],
      showPassword: false,
      showConfirmPassword: false,
      sexos: SEXOS,
      exames: TIPOS_EXAME,
      empresas: [],
      unidades: [],
      loading: false,
      disableFields: false,
      openRedirectDialog: false,
      loadingCpf: false,
      loadingEmpresas: false,
      loadingFuncoes: false,
      loadingSetores: false
    }
  },
  components: {
    CustomAutoComplete,
    RedirectToLoginDialog
  },
  methods: {
    async verificaCpf() {
      if (this.validaCpf(this.funcionario.cpf)) {
        try {
          this.loadingCpf = true
          const cpf = this.funcionario.cpf.replace(/[\s.-]*/gim, '')
          const response = await acessoRapidoService.verificaCpf(cpf)
          if (response.data.cadastroPendente) {
            this.disableFields = true

            const { data } = response
            this.funcionario = {
              ...this.funcionario,
              nome: data.nome,
              sexo: data.sexo,
              dtNascimento: data.dtNascimento,
              dtAdmissao: data.dtAdmissao,
              empresa: data.empresa,
              unidade: data.empresa.unidades ? data.empresa.unidades[0] : null,
              idFuncao: data.funcao.idFuncao,
              idSetor: data.setor.idSetor
            }

            this.empresas = [data.empresa]
            this.unidades = data.empresa.unidades || []
            this.funcoes = [data.funcao]
            this.setores = [data.setor]
          } else {
            this.redirectToLogin()
          }
        } catch ({ response }) {
          if (response.status === 404) {
            this.disableFields = false

            this.funcionario = {
              ...this.funcionario,
              nome: null,
              dtNascimento: null,
              dtAdmissao: null,
              sexo: null,
              idFuncao: null,
              idSetor: null,
              empresa: null,
              unidade: null
            }
          } else {
            this.$showErrorMessage(response.data.message)
          }
        } finally {
          this.loadingCpf = false
        }
      }
    },
    nextStep() {
      if (this.$refs.agendamentoForm.validate()) {
        this.$emit('next-step')
      }
    },
    previousStep() {
      this.$emit('previous-step')
    },
    validaCpf(cpf) {
      if (process.env.VUE_APP_ENV === 'DEV') return true
      return validaCpf(cpf)
    },
    validaUnidade() {
      if (this.unidades) {
        return !!this.funcionario.unidade
      } else {
        return true
      }
    },
    async loadEmpresas(busca) {
      try {
        this.loadingEmpresas = true
        const { data } = await acessoRapidoService.buscaEmpresas(busca)
        this.empresas = data
      } catch ({ response }) {
        this.$showErrorMessage(response.data.message)
      } finally {
        this.loadingEmpresas = false
      }
    },
    onChangeEmpresa() {
      this.verificaUnidades()
    },
    onChangeUnidade() {
      if (this.funcionario.unidade) {
        this.loadFuncoes(this.funcionario.unidade)
        this.loadSetores(this.funcionario.unidade)
      }
    },
    verificaUnidades() {
      this.funcionario.unidade = null
      if (this.funcionario.empresa && this.funcionario.empresa.unidades.length > 0) {
        this.unidades = [{ ...this.funcionario.empresa, razaoSocial: 'Matriz' }, ...this.funcionario.empresa.unidades]
        console.log('2')
      } else {
        console.log('1')
        this.unidades = []
        this.loadFuncoes(this.funcionario.empresa.idEmpresa)
        this.loadSetores(this.funcionario.empresa.idEmpresa)
      }
    },
    async loadFuncoes(idEmpresa) {
      try {
        this.loadingFuncoes = true
        this.funcionario.idFuncao = null
        const { data } = await acessoRapidoService.loadFuncoes(idEmpresa)
        this.funcoes = data
      } catch (error) {
        console.error(error)
      } finally {
        this.loadingFuncoes = false
      }
    },
    async loadSetores(idEmpresa) {
      try {
        this.loadingSetores = true
        this.funcionario.idSetor = null
        const { data } = await acessoRapidoService.loadSetores(idEmpresa)
        this.setores = data
      } catch (error) {
        console.error(error)
      } finally {
        this.loadingSetores = false
      }
    },
    redirectToLogin() {
      this.openRedirectDialog = true
    }
  },
  mounted() {
    if (this.funcionario.empresa) this.empresas.push(this.funcionario.empresa)
    this.disableFields = true
  }
}
</script>

<style scoped>
.search-text-field >>> .v-input__append-inner {
  margin: auto !important;
}
</style>
